import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import { useNotification } from '../../sharedComponents/Notifications';
import Pagination from '../components/Pagination';
import { Modal, MultiSelect } from '../components/adminModals';
import Drawer from '../../sharedComponents/Drawer';
import {
    categoryAdminService,
    primaFacieService,
    revisedCopyByDeskService,
    Category,
    CategoryMemberDropdownItem,
    RevisedCopyAssessmentProcessItem,
    RevisedCopyReviewerStatus,
    SubmissionRevisionRequest,
    submissionsByDeskService,
    DeskItem,
} from '../services/adminAuth';
import { useAuth } from '../../../auth/hooks/auth';
import { CheckCircle, Clock, XCircle, AlertTriangle, Eye, FileText, Download, Loader2, MessageSquare, ThumbsUp, ThumbsDown, RefreshCw } from 'lucide-react';
import { FILE_BASE_URL } from '../../../api/axios';
import { downloadFileFromUrl } from '../../../utils/downloadHelper';

// ─────────────────────────────────────────────────────────────────────────────

const RECOMMENDATION_MAP: Record<number, { label: string; positive: boolean }> = {
    3: { label: 'Acceptable for Publication', positive: true },
    5: { label: 'Process further to Author for Revised Copy', positive: false },
};

// Panel member recommendation mapping
const PANEL_RECOMMENDATION_MAP: Record<string, { label: string; color: string }> = {
    'Accept': { label: 'Accept', color: 'text-green-700 bg-green-50' },
    'NeedRevision': { label: 'Need Revision', color: 'text-amber-700 bg-amber-50' },
    'Reject': { label: 'Reject', color: 'text-red-700 bg-red-50' },
    'Pending': { label: 'Pending', color: 'text-gray-500 bg-gray-100' },
};

const resolveUrl = (url: string) =>
    url.startsWith('http') ? url : `${FILE_BASE_URL}/${url}`;

const formatDate = (iso: string | null | undefined) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Spinner = () => (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
);

const getStatusChip = (status: string) => {
    const lower = status?.toLowerCase();
    if (lower === 'completed' || lower === 'submitted') {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-green-50 text-green-700">
                <CheckCircle className="w-3 h-3" />{status}
            </span>
        );
    }
    if (lower === 'pending' || lower === 'Not Started' || lower === 'notstarted') {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700">
                <Clock className="w-3 h-3" />Pending
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600">
            <XCircle className="w-3 h-3" />{status}
        </span>
    );
};

const getPanelRecommendationChip = (recommendation: string | undefined) => {
    if (!recommendation || recommendation === 'Pending') {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-500">
                <Clock className="w-3 h-3" />Pending
            </span>
        );
    }
    const config = PANEL_RECOMMENDATION_MAP[recommendation];
    if (config) {
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${config.color}`}>
                {config.label}
            </span>
        );
    }
    return null;
};

// ─────────────────────────────────────────────────────────────────────────────

function RevisionAssessmentReports() {
    const { showNotification } = useNotification();
    const { user } = useAuth();

    const isCategoryAdmin = user?.role === 'thematicarealeader';

    // ── Bootstrap ─────────────────────────────────────────────────────────────
    const [categories, setCategories] = useState<Category[]>([]);
    const [categoryAdminCategoryId, setCategoryAdminCategoryId] = useState<number | null>(null);
    const [initialized, setInitialized] = useState(false);

    // ── Table state ───────────────────────────────────────────────────────────
    const [items, setItems] = useState<RevisedCopyAssessmentProcessItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // ── Filters ───────────────────────────────────────────────────────────────
    const [search, setSearch] = useState('');
    const [completedFilter, setCompletedFilter] = useState<boolean>(isCategoryAdmin ? false : true);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number>(0);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // ── Desk list for final decision ──────────────────────────────────────────
    const [deskList, setDeskList] = useState<DeskItem[]>([]);
    const [deskListLoading, setDeskListLoading] = useState(false);

    // ── Revision History Drawer ───────────────────────────────────────────────
    const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
    const [historyTarget, setHistoryTarget] = useState<RevisedCopyAssessmentProcessItem | null>(null);
    const [historyItems, setHistoryItems] = useState<SubmissionRevisionRequest[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [downloadingMap, setDownloadingMap] = useState<Record<string, boolean>>({});

    // ── Add Panel Member modal ────────────────────────────────────────────────
    const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
    const [addPanelTarget, setAddPanelTarget] = useState<RevisedCopyAssessmentProcessItem | null>(null);
    const [addPanelMembers, setAddPanelMembers] = useState<CategoryMemberDropdownItem[]>([]);
    const [fetchingAddMembers, setFetchingAddMembers] = useState(false);
    const [selectedAddMemberIds, setSelectedAddMemberIds] = useState<(string | number)[]>([]);
    const [isAddingPanel, setIsAddingPanel] = useState(false);

    // ── Delete Panel Member modal ─────────────────────────────────────────────
    const [isDeletePanelOpen, setIsDeletePanelOpen] = useState(false);
    const [deletePanelTarget, setDeletePanelTarget] = useState<RevisedCopyAssessmentProcessItem | null>(null);
    const [selectedDeleteReviewId, setSelectedDeleteReviewId] = useState('');
    const [isDeletingMember, setIsDeletingMember] = useState(false);

    // ── Make Final Decision modal ─────────────────────────────────────────────
    const [isFinalDecisionOpen, setIsFinalDecisionOpen] = useState(false);
    const [finalDecisionTarget, setFinalDecisionTarget] = useState<RevisedCopyAssessmentProcessItem | null>(null);
    const [fdRecommendation, setFdRecommendation] = useState<'' | '3' | '5'>('');
    const [fdComment, setFdComment] = useState('');
    const [fdToWorkflowStage, setFdToWorkflowStage] = useState<number | ''>('');
    const [isSubmittingFinalDecision, setIsSubmittingFinalDecision] = useState(false);

    // ── View Reviewer Details Drawer ─────────────────────────────────────────
    const [isReviewerDetailsOpen, setIsReviewerDetailsOpen] = useState(false);
    const [selectedReviewer, setSelectedReviewer] = useState<RevisedCopyReviewerStatus | null>(null);

    // ── Bootstrap ─────────────────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            try {
                const res = await categoryAdminService.getCategoriesForCategoryAdmin();
                if (res.isSuccess && res.data) {
                    setCategories(res.data);
                    if (isCategoryAdmin) {
                        if (res.data.length > 0) {
                            const id = res.data[0].id;
                            setCategoryAdminCategoryId(id);
                            setSelectedCategoryId(id);
                        } else {
                            setCategoryAdminCategoryId(0);
                            setLoading(false);
                        }
                    } else {
                        setCategoryAdminCategoryId(0);
                    }
                } else {
                    if (isCategoryAdmin) { setCategoryAdminCategoryId(0); setLoading(false); }
                }
            } catch {
                if (isCategoryAdmin) { setCategoryAdminCategoryId(0); setLoading(false); }
            } finally {
                setInitialized(true);
            }
        };
        init();
    }, [isCategoryAdmin]);

    // ── Fetch ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!initialized) return;
        if (isCategoryAdmin && categoryAdminCategoryId === null) return;
        if (isCategoryAdmin && categoryAdminCategoryId === 0) return;

        const handler = setTimeout(() => { fetchItems(); }, 400);
        return () => clearTimeout(handler);
    }, [initialized, currentPage, pageSize, search, completedFilter, selectedCategoryId, categoryAdminCategoryId]);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const response = await revisedCopyByDeskService.getRevisedCopyAssessmentProcess({
                pageNumber: currentPage,
                pageSize,
                sortBy: '',
                sortDescending: true,
                search,
                completed: isCategoryAdmin ? completedFilter : true,
                categoryId: isCategoryAdmin ? (categoryAdminCategoryId ?? 0) : selectedCategoryId,
            });
            if (response.isSuccess && response.data) {
                setItems(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch revision assessments' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An error occurred while fetching revision assessments' });
        } finally {
            setLoading(false);
        }
    };

    // ── Revision History Drawer ───────────────────────────────────────────────
    const openHistoryDrawer = async (row: RevisedCopyAssessmentProcessItem) => {
        setHistoryTarget(row);
        setHistoryItems([]);
        setIsHistoryDrawerOpen(true);
        setHistoryLoading(true);
        try {
            const res = await revisedCopyByDeskService.getRevisionRequestsBySubmissionId(row.submissionId);
            if (res.isSuccess && res.data) {
                setHistoryItems(res.data);
            } else {
                showNotification('error', { title: 'Error', message: res.message || 'Failed to load revision history' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleDownload = async (url: string, key: string, filename: string) => {
        setDownloadingMap(prev => ({ ...prev, [key]: true }));
        try {
            await downloadFileFromUrl(resolveUrl(url), filename);
        } finally {
            setDownloadingMap(prev => ({ ...prev, [key]: false }));
        }
    };

    // ── Add Panel handlers ────────────────────────────────────────────────────
    const openAddPanelModal = async (row: RevisedCopyAssessmentProcessItem) => {
        setAddPanelTarget(row);
        setSelectedAddMemberIds([]);
        setIsAddPanelOpen(true);
        setFetchingAddMembers(true);
        try {
            const categoryId = isCategoryAdmin ? (categoryAdminCategoryId ?? 0) : 0;
            const response = await primaFacieService.getCategoryMemberDropdown(categoryId);
            if (response.isSuccess && response.data) {
                const existingIds = new Set(row.reviewerStatuses.map(r => r.memberId));
                setAddPanelMembers(response.data.filter(m => !existingIds.has(m.id)));
            } else {
                setAddPanelMembers([]);
                showNotification('error', { title: 'Error', message: response.message || 'Failed to load members' });
            }
        } catch {
            setAddPanelMembers([]);
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
        } finally {
            setFetchingAddMembers(false);
        }
    };

    const closeAddPanelModal = () => {
        setIsAddPanelOpen(false);
        setAddPanelTarget(null);
        setSelectedAddMemberIds([]);
        setAddPanelMembers([]);
    };

    const handleAddPanelMembers = async () => {
        if (!addPanelTarget || selectedAddMemberIds.length === 0) return;
        setIsAddingPanel(true);
        try {
            const assignedMembers = selectedAddMemberIds.map(id => {
                const member = addPanelMembers.find(m => m.id === id);
                return { memberId: id.toString(), name: member?.name ?? '' };
            });
            const res = await revisedCopyByDeskService.assignMembersForRevisedCopyReview({
                submissionRevisionRequestId: addPanelTarget.submissionRevisionRequestId,
                assignedMembers,
            });
            if (res.isSuccess) {
                const failed = (res.data ?? []).filter(r => !r.successful);
                if (failed.length > 0) {
                    showNotification('error', {
                        title: 'Partial Assignment',
                        message: `${failed.length} member(s) could not be assigned: ${failed.map(f => f.reason).join(', ')}`,
                    });
                } else {
                    showNotification('success', { title: 'Success', message: 'Panel member(s) added successfully' });
                }
                closeAddPanelModal();
                fetchItems();
            } else {
                showNotification('error', { title: 'Error', message: res.message || 'Failed to add members' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred' });
        } finally {
            setIsAddingPanel(false);
        }
    };

    // ── Delete Panel handlers ─────────────────────────────────────────────────
    const openDeletePanelModal = (row: RevisedCopyAssessmentProcessItem) => {
        setDeletePanelTarget(row);
        setSelectedDeleteReviewId('');
        setIsDeletePanelOpen(true);
    };

    const closeDeletePanelModal = () => {
        if (isDeletingMember) return;
        setIsDeletePanelOpen(false);
        setDeletePanelTarget(null);
        setSelectedDeleteReviewId('');
    };

    const handleDeletePanelMember = async () => {
        if (!selectedDeleteReviewId) {
            showNotification('error', { title: 'Required', message: 'Please select a member to remove.' });
            return;
        }
        setIsDeletingMember(true);
        try {
            const response = await revisedCopyByDeskService.deleteRevisedCopyReviewAssignment(selectedDeleteReviewId);
            if (response?.isSuccess) {
                showNotification('success', { title: 'Removed', message: 'Panel member removed successfully.' });
                closeDeletePanelModal();
                fetchItems();
            } else {
                showNotification('error', { title: 'Error', message: response?.message || 'Failed to remove member.' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred.' });
        } finally {
            setIsDeletingMember(false);
        }
    };

    // ── Final Decision handlers ───────────────────────────────────────────────
    const openFinalDecisionModal = async (row: RevisedCopyAssessmentProcessItem) => {
        const hasIncomplete = row.reviewerStatuses.some(
            r => r.status?.toLowerCase() !== 'completed' && r.status?.toLowerCase() !== 'submitted'
        );
        if (hasIncomplete) {
            showNotification('error', {
                title: 'Cannot Make Final Decision',
                message: 'All panel assessments must be completed before making a final decision.',
            });
            return;
        }
        if (row.reviewerStatuses.length === 0) {
            showNotification('error', {
                title: 'No Panel Assigned',
                message: 'Please assign panel members before making a final decision.',
            });
            return;
        }
        setFinalDecisionTarget(row);
        setFdRecommendation('');
        setFdComment('');
        setFdToWorkflowStage('');
        setIsFinalDecisionOpen(true);

        // Load desk list for workflow stage selection
        setDeskListLoading(true);
        try {
            const deskRes = await submissionsByDeskService.getDeskList();
            setDeskList(deskRes.isSuccess && deskRes.data ? deskRes.data : []);
        } catch {
            setDeskList([]);
            showNotification('error', { title: 'Error', message: 'Failed to load desk list' });
        } finally {
            setDeskListLoading(false);
        }
    };

    const closeFinalDecisionModal = () => {
        if (isSubmittingFinalDecision) return;
        setIsFinalDecisionOpen(false);
        setFinalDecisionTarget(null);
        setFdRecommendation('');
        setFdComment('');
        setFdToWorkflowStage('');
        setDeskList([]);
    };

    const handleSubmitFinalDecision = async () => {
        if (!finalDecisionTarget || !fdRecommendation) {
            showNotification('error', { title: 'Required', message: 'Please select a recommendation.' });
            return;
        }
        if (fdToWorkflowStage === '') {
            showNotification('error', { title: 'Required', message: 'Please select the next desk to move to.' });
            return;
        }
        setIsSubmittingFinalDecision(true);
        try {
            const res = await revisedCopyByDeskService.submitFinalDecisionOnRevisedCopyReview({
                submissionRevisionRequestId: finalDecisionTarget.submissionRevisionRequestId,
                comment: fdComment,
                recommendation: parseInt(fdRecommendation, 10),
                toWorkflowStage: fdToWorkflowStage as number,
            });
            if (res.isSuccess) {
                showNotification('success', { title: 'Decision Submitted', message: 'The final revision assessment decision has been submitted.' });
                closeFinalDecisionModal();
                fetchItems();
            } else {
                showNotification('error', { title: 'Submission Failed', message: res.message || 'Failed to submit final decision.' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred.' });
        } finally {
            setIsSubmittingFinalDecision(false);
        }
    };

    // ── Open reviewer details drawer ──────────────────────────────────────────
    const openReviewerDetails = (reviewer: RevisedCopyReviewerStatus) => {
        setSelectedReviewer(reviewer);
        setIsReviewerDetailsOpen(true);
    };

    // ── Filter handlers ───────────────────────────────────────────────────────
    const handleSearch = (query: string) => { setSearch(query); setCurrentPage(1); };
    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePageSizeChange = (size: number) => { setPageSize(size); setCurrentPage(1); };
    const handleClearFilters = () => {
        setSearch('');
        setCompletedFilter(isCategoryAdmin ? false : true);
        if (!isCategoryAdmin) setSelectedCategoryId(0);
        setCurrentPage(1);
    };

    // ── Helpers ───────────────────────────────────────────────────────────────
    const isFinalDecisionMade = (row: RevisedCopyAssessmentProcessItem) =>
        row.status === 'AcceptableForPublication' || row.status === 'ReturnToAuthorForRevisedCopy';

    const getRecommendationFromStatus = (status: string): number | null => {
        if (status === 'AcceptableForPublication') return 3;
        if (status === 'ReturnToAuthorForRevisedCopy') return 5;
        return null;
    };

    // ── Columns ───────────────────────────────────────────────────────────────
    const columns: TableColumn<RevisedCopyAssessmentProcessItem>[] = [
        {
            key: 'submissionNumber',
            label: 'ID',
            render: (_, row) => <span className="text-gray-500 text-sm font-mono">{row.submissionNumber}</span>,
        },
        {
            key: 'submissionTitle',
            label: 'Title / Authors',
            render: (_, row) => {
                const authors = row.bookAuthors.length > 0
                    ? row.bookAuthors.map(a => a.name).join(', ')
                    : '—';
                return (
                    <div>
                        <div className="font-medium text-gray-900 mb-0.5 leading-snug">{row.submissionTitle}</div>
                        <div className="text-xs text-gray-500">{authors}</div>
                    </div>
                );
            },
        },
        {
            key: 'status',
            label: 'Recommendation',
            render: (_, row) => {
                const recNum = getRecommendationFromStatus(row.status);
                if (recNum !== null) {
                    const cfg = RECOMMENDATION_MAP[recNum];
                    return (
                        <div className="flex flex-col gap-1">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold text-green-700">
                                <CheckCircle className="w-3.5 h-3.5" />Final Decision Made
                            </span>
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${cfg.positive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                                {cfg.label}
                            </span>
                        </div>
                    );
                }
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-700">
                        <Clock className="w-3.5 h-3.5" />Pending
                    </span>
                );
            },
        },
        {
            key: 'reviewerStatuses',
            label: 'Assigned Panel',
            render: (_, row) => {
                const statuses: RevisedCopyReviewerStatus[] = row.reviewerStatuses ?? [];

                if (statuses.length === 0) {
                    return (
                        <span className="text-xs text-gray-400 italic">
                            No panel assigned
                        </span>
                    );
                }

                return (
                    <div className="space-y-1.5">
                        {statuses.map((r, idx) => {
                            const isCompleted = r.status?.toLowerCase() === 'completed' || r.status?.toLowerCase() === 'submitted';
                            return (
                                <div
                                    key={r.revisedCopyReviewId || r.memberId}
                                    className="flex items-center gap-2 min-h-[28px]"
                                >
                                    <div className="w-5 h-5 border rounded-full bg-gray-100 flex items-center justify-center text-gray-900 font-bold text-[10px] flex-shrink-0">
                                        {idx + 1}
                                    </div>

                                    <span className="text-xs text-gray-700 font-medium min-w-[120px]">
                                        {r.memberName}
                                    </span>

                                    <div className="flex items-center gap-1.5">
                                        {getStatusChip(r.status)}
                                        
                                        {/* Recommendation chip for completed reviewers */}
                                        {isCompleted && r.recommendation && (
                                            getPanelRecommendationChip(r.recommendation)
                                        )}

                                        {/* View details button for completed reviewers - always visible */}
                                        {isCompleted && (r.comment || r.recommendation) && (
                                            <button
                                                onClick={() => openReviewerDetails(r)}
                                                className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-[#088A61] transition-colors"
                                                title="View reviewer comments"
                                            >
                                                <Eye className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            },
        }
    ];

    // ── Actions ───────────────────────────────────────────────────────────────
    const actions: TableAction<RevisedCopyAssessmentProcessItem>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                {
                    label: 'View Revision History',
                    onClick: openHistoryDrawer,
                    className: 'text-blue-600',
                },
                {
                    label: 'Add Panel Member',
                    onClick: openAddPanelModal,
                    className: (row) =>
                        isFinalDecisionMade(row) ? 'hidden' : 'text-green-600',
                },
                {
                    label: 'Delete Panel Member',
                    onClick: openDeletePanelModal,
                    className: (row) => {
                        if (isFinalDecisionMade(row)) return 'hidden';
                        const hasRemovable = row.reviewerStatuses.some(
                            r => r.status?.toLowerCase() === 'not started' || r.status?.toLowerCase() === 'notstarted'
                        );
                        return hasRemovable ? 'text-red-600' : 'hidden';
                    },
                },
                {
                    label: 'Make Final Decision',
                    onClick: openFinalDecisionModal,
                    className: (row) =>
                        isFinalDecisionMade(row) ? 'hidden' : 'text-blue-600',
                },
            ],
            title: 'Actions',
        },
    ];

    // ── Category dropdown for filter ──────────────────────────────────────────
    const renderCategoryDropdown = () => {
        if (isCategoryAdmin) {
            if (categoryAdminCategoryId === 0 || categories.length === 0) {
                return (
                    <select disabled className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-400 cursor-not-allowed">
                        <option>No Thematic Area</option>
                    </select>
                );
            }
            return (
                <select disabled value={categories[0].id} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                    <option value={categories[0].id}>{categories[0].name}</option>
                </select>
            );
        }
        return (
            <select
                value={selectedCategoryId}
                onChange={(e) => { setSelectedCategoryId(parseInt(e.target.value)); setCurrentPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                <option value={0}>All Thematic Areas</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
        );
    };

    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Thematic Area</label>
                    {renderCategoryDropdown()}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assessment Status</label>
                    {isCategoryAdmin ? (
                        <select
                            value={completedFilter ? 'true' : 'false'}
                            onChange={(e) => { setCompletedFilter(e.target.value === 'true'); setCurrentPage(1); }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="false">In Progress</option>
                            <option value="true">Completed</option>
                        </select>
                    ) : (
                        <select disabled className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                            <option value="true">Completed</option>
                        </select>
                    )}
                </div>
            </div>
            <button onClick={handleClearFilters} className="mt-4 text-sm text-blue-600 hover:underline">
                Clear filters
            </button>
        </div>
    );

    const emptyMessage = isCategoryAdmin && categoryAdminCategoryId === 0
        ? 'No Thematic Area assigned to this admin yet'
        : 'No revision assessments found.';

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="w-full">
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Revision Assessment Reports</h1>
                <p className="text-gray-500 text-sm sm:text-base">
                    Track revised submissions currently under the revision assessment process.
                </p>
            </div>

            <ReusableTable
                title="Revision Assessment Process"
                subTitle="Revised submissions and their panel assessment status"
                columns={columns}
                data={items}
                actions={actions}
                loading={loading}
                searchPlaceholder="Search by title or submission number..."
                onSearch={handleSearch}
                emptyMessage={emptyMessage}
                showSearch={true}
                showFilter={true}
                filterPanel={filterPanel}
                onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
                isFilterOpen={isFilterOpen}
            />

            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            )}

            {/* ── Revision History Drawer ───────────────────────────────────── */}
            <Drawer
                isOpen={isHistoryDrawerOpen}
                onClose={() => { setIsHistoryDrawerOpen(false); setHistoryTarget(null); setHistoryItems([]); }}
                title="Revision History"
                width="max-w-xl"
            >
                {historyLoading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="w-6 h-6 border-2 border-gray-200 border-t-[#088A61] rounded-full animate-spin" />
                    </div>
                ) : historyItems.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-12">No revision records found.</p>
                ) : (
                    <div className="space-y-5 pb-10">
                        <p className="text-sm text-gray-500">
                            {historyItems.length} revision request{historyItems.length !== 1 ? 's' : ''} for this submission
                        </p>
                        {historyItems.map((item, idx) => (
                            <div key={item.submissionRevisionRequestId} className="border border-gray-100 rounded-xl p-5 bg-white shadow-sm space-y-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900">{item.submissionTitle}</p>
                                        <p className="text-xs text-gray-400 font-mono mt-0.5">{item.submissionNumber}</p>
                                    </div>
                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${
                                        item.status === 'AcceptableForPublication'
                                            ? 'bg-green-50 text-green-700 border-green-200'
                                            : item.status === 'Submitted'
                                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                                : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                    }`}>
                                        {item.status}
                                    </span>
                                </div>

                                {item.bookAuthors.length > 0 && (
                                    <div>
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Authors</p>
                                        <p className="text-sm text-gray-700">{item.bookAuthors.map(a => a.name).join(', ')}</p>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Requested</p>
                                        <p className="text-sm text-gray-700">{formatDate(item.requestedAt)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Submitted</p>
                                        <p className="text-sm text-gray-700">{formatDate(item.submittedAt)}</p>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    {item.reviewedManuscriptUrl && (
                                        <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5">
                                            <div className="flex items-center gap-2">
                                                <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                <span className="text-xs font-medium text-gray-700">Reviewed Manuscript</span>
                                            </div>
                                            <button
                                                disabled={!!downloadingMap[`reviewed-${idx}`]}
                                                onClick={() => handleDownload(item.reviewedManuscriptUrl, `reviewed-${idx}`, `${item.submissionNumber}_reviewed.docx`)}
                                                className="flex items-center gap-1 text-xs px-2.5 py-1 bg-white border border-blue-200 rounded-lg text-blue-700 hover:bg-blue-50 disabled:opacity-60 transition-colors"
                                            >
                                                {downloadingMap[`reviewed-${idx}`]
                                                    ? <><Loader2 className="w-3 h-3 animate-spin" />Downloading...</>
                                                    : <><Download className="w-3 h-3" />Download</>
                                                }
                                            </button>
                                        </div>
                                    )}
                                    {item.revisedVersionUrl && (
                                        <div className="flex items-center justify-between bg-green-50 border border-green-100 rounded-lg px-3 py-2.5">
                                            <div className="flex items-center gap-2">
                                                <FileText className="w-4 h-4 text-green-600 flex-shrink-0" />
                                                <span className="text-xs font-medium text-gray-700">Revised Version (Author)</span>
                                            </div>
                                            <button
                                                disabled={!!downloadingMap[`revised-${idx}`]}
                                                onClick={() => handleDownload(item.revisedVersionUrl!, `revised-${idx}`, `${item.submissionNumber}_revised_v${idx + 1}.docx`)}
                                                className="flex items-center gap-1 text-xs px-2.5 py-1 bg-white border border-green-200 rounded-lg text-green-700 hover:bg-green-50 disabled:opacity-60 transition-colors"
                                            >
                                                {downloadingMap[`revised-${idx}`]
                                                    ? <><Loader2 className="w-3 h-3 animate-spin" />Downloading...</>
                                                    : <><Download className="w-3 h-3" />Download</>
                                                }
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {item.comment && (
                                    <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Admin Comment</p>
                                        <p className="text-sm text-gray-700">{item.comment}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Drawer>

            {/* ── Reviewer Details Drawer ────────────────────────────────────── */}
            <Modal
                isOpen={isReviewerDetailsOpen}
                onClose={() => { setIsReviewerDetailsOpen(false); setSelectedReviewer(null); }}
                title="Panel Member Assessment Details"
                description="Assessment details provided by the panel member for this revision assessment process."
                maxWidth="md"
            >
                {selectedReviewer && (
                    <div className="space-y-6">
                        <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-full bg-[#088A61]/10 flex items-center justify-center">
                                    <span className="text-[#088A61] font-semibold text-sm">
                                        {selectedReviewer.memberName.charAt(1).toUpperCase()}
                                    </span>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900">{selectedReviewer.memberName}</h3>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Status</p>
                                    {getStatusChip(selectedReviewer.status)}
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Recommendation</p>
                                    {getPanelRecommendationChip(selectedReviewer.recommendation) || (
                                        <span className="text-xs text-gray-400">—</span>
                                    )}
                                </div>
                            </div>
                            
                            {selectedReviewer.reviewedAt && (
                                <div className="mb-4">
                                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Reviewed At</p>
                                    <p className="text-sm text-gray-700">{formatDate(selectedReviewer.reviewedAt)}</p>
                                </div>
                            )}
                            
                            {selectedReviewer.comment && (
                                <div>
                                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                                        <MessageSquare className="w-3 h-3" /> Comment
                                    </p>
                                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedReviewer.comment}</p>
                                    </div>
                                </div>
                            )}
                            
                            {!selectedReviewer.comment && !selectedReviewer.recommendation && (
                                <p className="text-sm text-gray-400 italic text-center py-4">
                                    No additional details available from this panel member.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            {/* ── Add Panel Member Modal ────────────────────────────────────── */}
            <Modal
                isOpen={isAddPanelOpen}
                onClose={closeAddPanelModal}
                title="Add Panel Member"
                description="Select one or more thematic area members to add to the revision assessment panel."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {addPanelTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{addPanelTarget.submissionTitle}</p>
                            <p className="text-xs text-gray-400 font-mono mt-0.5">{addPanelTarget.submissionNumber}</p>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Members to Add <span className="text-red-500">*</span>
                        </label>
                        {fetchingAddMembers ? (
                            <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                <Spinner />Loading members…
                            </div>
                        ) : addPanelMembers.length === 0 ? (
                            <div className="w-full px-3 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 text-center italic">
                                No additional members available for this thematic area
                            </div>
                        ) : (
                            <MultiSelect
                                options={addPanelMembers.map(m => ({ value: m.id, label: m.name }))}
                                selectedValues={selectedAddMemberIds}
                                onChange={setSelectedAddMemberIds}
                                placeholder="Select members to add..."
                            />
                        )}
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button onClick={closeAddPanelModal} disabled={isAddingPanel}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Cancel
                        </button>
                        <button onClick={handleAddPanelMembers} disabled={isAddingPanel || selectedAddMemberIds.length === 0}
                            className="px-5 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                            {isAddingPanel ? <><Spinner />Adding…</> : 'Add Member(s)'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Delete Panel Member Modal ─────────────────────────────────── */}
            <Modal
                isOpen={isDeletePanelOpen}
                onClose={closeDeletePanelModal}
                title="Remove Panel Member"
                description="Only members who have not yet started their assessment can be removed."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {deletePanelTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{deletePanelTarget.submissionTitle}</p>
                        </div>
                    )}
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                        <p className="text-xs text-red-700 font-medium">This will permanently remove the member from this revision assessment panel.</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Select Member to Remove <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedDeleteReviewId}
                            onChange={e => setSelectedDeleteReviewId(e.target.value)}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        >
                            <option value="">Select member…</option>
                            {deletePanelTarget?.reviewerStatuses
                                .filter(r => r.status?.toLowerCase() === 'not started' || r.status?.toLowerCase() === 'notstarted')
                                .map(r => (
                                    <option key={r.revisedCopyReviewId} value={r.revisedCopyReviewId}>
                                        {r.memberName}
                                    </option>
                                ))
                            }
                        </select>
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button onClick={closeDeletePanelModal} disabled={isDeletingMember}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            Cancel
                        </button>
                        <button onClick={handleDeletePanelMember} disabled={isDeletingMember || !selectedDeleteReviewId}
                            className="px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                            {isDeletingMember ? <><Spinner />Removing…</> : 'Remove Member'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Make Final Decision Modal ─────────────────────────────────── */}
            <Modal
                isOpen={isFinalDecisionOpen}
                onClose={closeFinalDecisionModal}
                title="Make Final Revision Assessment Decision"
                description="This decision is final and cannot be undone. Review carefully before submitting."
                maxWidth="md"
            >
                <div className="space-y-3 mt-1">
                    {finalDecisionTarget && (
                        <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Submission</p>
                            <p className="text-sm font-semibold text-gray-900 line-clamp-1">{finalDecisionTarget.submissionTitle}</p>
                            <p className="text-xs text-gray-400 font-mono">{finalDecisionTarget.submissionNumber}</p>
                        </div>
                    )}

                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                        <p className="text-xs text-amber-700 font-medium">
                            This is <strong>irreversible</strong>. Ensure all panel assessments have been considered.
                        </p>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Recommendation <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={fdRecommendation}
                            onChange={e => setFdRecommendation(e.target.value as '' | '3' | '5')}
                            className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent"
                        >
                            <option value="">Select…</option>
                            <option value="3">Acceptable for Publication</option>
                            <option value="5">Return To Author For Revised Copy</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Move to Next Desk <span className="text-red-500">*</span>
                        </label>
                        {deskListLoading ? (
                            <div className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                <Spinner />Loading desks…
                            </div>
                        ) : (
                            <select
                                value={fdToWorkflowStage}
                                onChange={e => setFdToWorkflowStage(e.target.value === '' ? '' : Number(e.target.value))}
                                className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent"
                            >
                                <option value="">Select desk…</option>
                                {deskList.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={fdComment}
                            onChange={e => setFdComment(e.target.value)}
                            placeholder="Add any notes about this decision…"
                            rows={3}
                            className="block w-full px-2.5 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent resize-none"
                        />
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                        <button
                            onClick={closeFinalDecisionModal}
                            disabled={isSubmittingFinalDecision}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmitFinalDecision}
                            disabled={isSubmittingFinalDecision || !fdRecommendation || fdToWorkflowStage === ''}
                            className="px-5 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isSubmittingFinalDecision ? <><Spinner />Submitting…</> : 'Submit Final Decision'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default RevisionAssessmentReports;